import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  IconExternalLinkComponent,
  IconPhoneComponent,
  IconWhatsappComponent,
} from '@pedix-workspace/angular-ui-icons';
import { ApiV1_Establishment, ApiV1_Subsidiary } from '@pedix-workspace/api-v1';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import {
  WorkingHoursMessageDetailsComponent,
  EstablishmentOpenedStatusTagComponent,
  WorkingHoursMessageDialogComponent,
} from '@pedix-workspace/pedixapp-shared-catalog';
import { StadiaMapsStaticComponent } from '@pedix-workspace/angular-ui-map';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { WhatsappLinkService } from '@pedix-workspace/angular-utils';

@Component({
  selector: 'app-establishment-info',
  templateUrl: './establishment-info.component.html',
  styleUrls: ['./establishment-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslocoDirective,
    FormsModule,
    ButtonComponent,
    WorkingHoursMessageDetailsComponent,
    IconExternalLinkComponent,
    StadiaMapsStaticComponent,
    EstablishmentOpenedStatusTagComponent,
    IconPhoneComponent,
    IconWhatsappComponent,
  ],
})
export class EstablishmentInfoComponent {
  private modalDialogService = inject(ModalDialogService);
  private whatsappLinkService = inject(WhatsappLinkService);

  readonly establishment = input.required<ApiV1_Establishment | ApiV1_Subsidiary>();

  readonly displayName = input<boolean>();
  readonly displayAddress = input<boolean>();
  readonly displayMap = input<boolean>();
  readonly displayContactInfo = input<boolean>();
  readonly displayWorkingDays = input<boolean>();
  readonly displayOpenStatus = input<boolean>();

  readonly address = computed(() => {
    const establishment = this.establishment();

    return establishment.address || establishment.addressDetails?.freeSearch;
  });
  readonly addressReferences = computed(() => {
    const establishment = this.establishment();

    return 'addressReferences' in establishment ? establishment?.addressReferences : undefined;
  });
  readonly coordinates = computed(() => this.establishment().addressDetails?.coordinates);

  readonly establishmentMapUrl = computed(() => {
    const coordinates = this.coordinates();

    if (!coordinates) {
      return undefined;
    }
    const zoomLevel = 15;

    return `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}&zoom=${zoomLevel}`;
  });
  readonly hasContactInfo = computed(() => {
    return this.establishment().phone || this.establishment().additionalPhone;
  });
  readonly workingDaysEnabled = computed(() => this.establishment().workingDays?.isActive);

  readonly whatsappLink = computed(() => {
    return this.whatsappLinkService.getTrustedLink({
      phone: this.establishment().phone || '',
    });
  });

  get whatsappLinkHttpTarget() {
    return this.whatsappLinkService.getWhatsappLinkHttpTarget();
  }

  displayWorkingHours(event: Event) {
    event.stopPropagation();

    this.modalDialogService
      .attachDialog(WorkingHoursMessageDialogComponent)
      .open(this.establishment().workingDays);
  }
}
